import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Popup, useMap } from 'react-leaflet';
import { divIcon } from 'leaflet';
import { fmtTime, fmtDateTime } from '../utils/date';
import type { PatrolPoint, AlarmEvent, IncidentEvent } from '../types';

interface Props {
  points: PatrolPoint[];
  colour: string;
  guardName: string;
  alarms?: AlarmEvent[];
  incidents?: IncidentEvent[];
  className?: string;
}

const alarmMarkerIcon = divIcon({
  className: '',
  html: `<div style="background:#dc2626;color:#fff;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-size:16px;box-shadow:0 2px 6px rgba(0,0,0,.5);border:2px solid #fff;">🚨</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -18],
});

const incidentMarkerIcon = divIcon({
  className: '',
  html: `<div style="background:#f59e0b;color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:14px;box-shadow:0 2px 6px rgba(0,0,0,.4);border:2px solid #fff;">⚠️</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -16],
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Backend sends recorded_at as Unix seconds integer; convert to ms for Date. */
function toMs(recorded_at: string | number): number {
  if (typeof recorded_at === 'number') return recorded_at * 1000;
  const n = Number(recorded_at);
  return Number.isFinite(n) && n < 1e12 ? n * 1000 : new Date(recorded_at).getTime();
}

function segmentSpeedKmh(p1: PatrolPoint, p2: PatrolPoint): number {
  const distM = haversineM(p1.lat, p1.lng, p2.lat, p2.lng);
  const timeSec = (toMs(p2.recorded_at) - toMs(p1.recorded_at)) / 1000;
  if (timeSec <= 0 || distM < 0.5) return 0;
  return (distM / 1000) / (timeSec / 3600);
}

/**
 * Maps a normalised value t∈[0,1] to a hue on the blue→green→red spectrum.
 * 0 = slowest (dark blue), 0.5 = average (green), 1 = fastest (red).
 */
function tToColor(t: number): string {
  const hue = Math.round(240 - t * 240);
  return `hsl(${hue}, 90%, 46%)`;
}

/** Formats speed in km/h as a pace string "MM:SS /km". */
function formatPace(kmh: number): string {
  if (kmh <= 0) return '—';
  const secPerKm = 3600 / kmh;
  const min = Math.floor(secPerKm / 60);
  const sec = Math.round(secPerKm % 60);
  return `${min}:${sec.toString().padStart(2, '0')} /km`;
}

// ── Map helpers ───────────────────────────────────────────────────────────────

function FitBounds({ positions }: { positions: Array<[number, number]> }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 1) map.fitBounds(positions, { padding: [40, 40] });
    else if (positions.length === 1) map.setView(positions[0], 16);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PatrolReplay({ points, guardName, alarms = [], incidents = [], className = 'h-full w-full' }: Props) {
  const [cursor, setCursor] = useState(points.length - 1);
  const [playing, setPlaying] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const allPositions = useMemo<[number, number][]>(
    () => points.map((p) => [p.lat, p.lng]),
    [points],
  );

  // ── Speed computation ────────────────────────────────────────────────────────

  /** segmentSpeeds[i] = km/h between points[i] and points[i+1] */
  const segmentSpeeds = useMemo(
    () => points.slice(0, -1).map((p, i) => segmentSpeedKmh(p, points[i + 1])),
    [points],
  );

  /**
   * Speed scale is relative to THIS patrol:
   * - Moving segments only (> 0.4 km/h)
   * - 5th–95th percentile to clip outliers
   * - avg is the true mean of all moving segments
   */
  const { minSpeed, maxSpeed, avgSpeed } = useMemo(() => {
    const moving = segmentSpeeds.filter((s) => s > 0.4);
    if (moving.length === 0) return { minSpeed: 0, maxSpeed: 1, avgSpeed: 0 };
    const sorted = [...moving].sort((a, b) => a - b);
    const p5  = sorted[Math.max(0, Math.floor(sorted.length * 0.05))];
    const p95 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))];
    const avg = moving.reduce((a, b) => a + b, 0) / moving.length;
    return { minSpeed: p5, maxSpeed: p95 > p5 ? p95 : p5 + 0.1, avgSpeed: avg };
  }, [segmentSpeeds]);

  /** Pre-computed dot colour for each point (= speed of segment leaving that point). */
  const dotColors = useMemo(() => {
    const range = maxSpeed - minSpeed;
    return segmentSpeeds.map((s) => {
      if (s <= 0.4) return '#d1d5db'; // stationary — light gray
      const t = range > 0 ? Math.min(1, Math.max(0, (s - minSpeed) / range)) : 0.5;
      return tToColor(t);
    });
  }, [segmentSpeeds, minSpeed, maxSpeed]);

  // ── Playback ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (playing) {
      timerRef.current = setInterval(() => {
        setCursor((c) => {
          if (c >= points.length - 1) { setPlaying(false); return c; }
          return c + 1;
        });
      }, 80);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [playing, points.length]);

  function handleScrub(e: React.ChangeEvent<HTMLInputElement>) {
    setPlaying(false);
    setCursor(Number(e.target.value));
  }

  function togglePlay() {
    if (cursor >= points.length - 1) setCursor(0);
    setPlaying((p) => !p);
  }

  if (points.length === 0) {
    return <div className={`${className} flex items-center justify-center text-gray-400`}>No track data</div>;
  }

  const currentPoint = points[cursor] ?? null;
  // Colour at the head marker = last completed segment's colour
  const headColor = cursor > 0 ? dotColors[cursor - 1] : '#d1d5db';

  return (
    <div className="flex flex-col h-full gap-2">
      <div className="flex-1 relative">
        <MapContainer
          center={allPositions[0] ?? [-33.9249, 18.4241]}
          zoom={15}
          className={className}
          style={{ zIndex: 0 }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds positions={allPositions} />

          {/* Ghost trail — full route, faint */}
          <Polyline
            positions={allPositions}
            pathOptions={{ color: '#9ca3af', weight: 3, opacity: 0.18, lineCap: 'round', lineJoin: 'round' }}
          />

          {/* Pace-coloured polyline segments for played portion */}
          {points.slice(0, cursor).map((p, i) => (
            <Polyline
              key={i}
              positions={[[p.lat, p.lng], [points[i + 1].lat, points[i + 1].lng]]}
              pathOptions={{
                color: dotColors[i] ?? '#d1d5db',
                weight: 6,
                opacity: 1,
                lineCap: 'round',
                lineJoin: 'round',
              }}
            />
          ))}

          {/* Head marker — current position */}
          {currentPoint && (
            <CircleMarker
              center={[currentPoint.lat, currentPoint.lng]}
              radius={7}
              pathOptions={{
                color: '#fff',
                weight: 2,
                fillColor: headColor,
                fillOpacity: 1,
              }}
            />
          )}

          {/* Alarm markers */}
          {alarms.map((a) => (
            <Marker key={`alarm-${a.id}`} position={[a.lat, a.lng]} icon={alarmMarkerIcon}>
              <Popup>
                <strong>🚨 Alarm</strong><br />
                <span className="text-xs text-gray-600">{fmtDateTime(a.raised_at)}</span>
                {a.notes && <><br /><span className="text-xs">{a.notes}</span></>}
                {a.ack_at && <><br /><span className="text-xs text-green-700">Acked {fmtDateTime(a.ack_at)}{a.ack_by_name ? ` by ${a.ack_by_name}` : ''}</span></>}
              </Popup>
            </Marker>
          ))}

          {/* Incident markers */}
          {incidents.map((inc) => (
            <Marker key={`incident-${inc.id}`} position={[inc.lat, inc.lng]} icon={incidentMarkerIcon}>
              <Popup>
                <strong>⚠️ Incident</strong><br />
                <span className="text-xs text-gray-600">{fmtDateTime(inc.raised_at)}</span>
                {inc.notes && <><br /><span className="text-xs">{inc.notes}</span></>}
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Gradient legend — Doggoes style */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[400] bg-white/92 backdrop-blur-sm rounded-xl shadow-md px-4 py-2.5 w-72">
          <div
            className="h-3 rounded-full w-full"
            style={{
              background:
                'linear-gradient(to right, hsl(240,90%,46%), hsl(200,90%,46%), hsl(160,85%,38%), hsl(120,85%,38%), hsl(75,90%,44%), hsl(45,90%,48%), hsl(15,90%,48%), hsl(0,90%,46%))',
            }}
          />
          <div className="flex justify-between items-center text-xs mt-1.5">
            <span className="text-gray-500">Slower</span>
            <span className="font-semibold text-gray-700">avg {formatPace(avgSpeed)}</span>
            <span className="text-gray-500">Faster</span>
          </div>
        </div>
      </div>

      {/* Timeline controls */}
      <div className="bg-white border rounded-lg px-4 py-3 flex items-center gap-4 shadow-sm">
        <button
          onClick={togglePlay}
          className="w-8 h-8 rounded-full bg-brand-600 text-white flex items-center justify-center hover:bg-brand-700 transition-colors flex-shrink-0"
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? '⏸' : '▶'}
        </button>

        <input
          type="range"
          min={0}
          max={points.length - 1}
          value={cursor}
          onChange={handleScrub}
          className="flex-1 accent-brand-600"
        />

        <div className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0 flex items-center gap-2">
          <span
            className="inline-block w-2 h-2 rounded-full flex-shrink-0"
            style={{ background: headColor }}
          />
          {currentPoint
            ? fmtTime(currentPoint.recorded_at)
            : '—'}
          {currentPoint?.source === 'offline' && (
            <span className="ml-1 px-1 bg-amber-100 text-amber-700 rounded text-xs">offline</span>
          )}
        </div>

        <div className="text-xs font-medium text-gray-700 flex-shrink-0 hidden sm:block">
          {guardName}
        </div>
      </div>
    </div>
  );
}
